// Annotation schema
// Discriminated union of 4 annotation types: text, image, dxf, geometric_shape
// Source: C++ annotation base class with 4 concrete subtypes

import { z } from 'zod';
import { RgbaSchema, PointSchema } from '../base/common';

// --- Geometric shape enums ---

const GeometricShapeTypeSchema = z.enum([
  'rectangle', 'oval', 'rhombus', 'arrow',
  'polygon', 'star', 'line',
]);

const ShapeDirectionSchema = z.enum([
  'pointed_right', 'pointed_down', 'pointed_left', 'pointed_up',
]);

const LineDirectionSchema = z.enum([
  'left_to_right', 'top_to_bottom', 'top_left_to_bottom_right', 'bottom_left_to_top_right',
]);

// --- Rect (bounding rectangle for geometric shapes) ---

const RectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

// --- Base annotation fields ---

const AnnotationBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  locked: z.boolean(),
  layer_id: z.string().optional(),
});

// --- Text Annotation ---

const TextAnnotationSchema = AnnotationBaseSchema.extend({
  type: z.literal('text'),
  text: z.string(),
  font: z.object({
    family: z.string().optional(),
    size: z.number().optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
  }).optional(),
  location: PointSchema,
  orientation: z.object({
    rotation: z.number().optional(),
    flip_horizontal: z.boolean().optional(),
    flip_vertical: z.boolean().optional(),
  }).optional(),
  color: RgbaSchema,
});

// --- Image Annotation ---

const ImageAnnotationSchema = AnnotationBaseSchema.extend({
  type: z.literal('image'),
  position: PointSchema,
  width: z.number(),
  height: z.number(),
  /** Raw image data — stored opaque for round-trip fidelity */
  image_data: z.unknown(),
});

// --- DXF Annotation ---

const DxfAnnotationSchema = AnnotationBaseSchema.extend({
  type: z.literal('dxf'),
  position: PointSchema,
  width: z.number(),
  height: z.number(),
  lines: z.array(z.object({
    start: PointSchema,
    end: PointSchema,
  })),
});

// --- Geometric Shape Annotation ---

const GeometricShapeAnnotationSchema = AnnotationBaseSchema.extend({
  type: z.literal('geometric_shape'),
  rect: RectSchema,
  shape: GeometricShapeTypeSchema,
  outline_color: RgbaSchema,
  outline_width: z.number(),
  /** Qt::PenStyle as integer */
  outline_style: z.number(),
  fill_color: RgbaSchema,
  /** Qt::BrushStyle as integer (0 = no fill) */
  fill_style: z.number(),
  rotation: z.number(),
  direction: ShapeDirectionSchema,
  number_of_sides: z.number(),
  line_direction: LineDirectionSchema,
});

// --- Annotation Union ---

export const AnnotationSchema = z.discriminatedUnion('type', [
  TextAnnotationSchema,
  ImageAnnotationSchema,
  DxfAnnotationSchema,
  GeometricShapeAnnotationSchema,
]);

export type Annotation = z.infer<typeof AnnotationSchema>;
